import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Calculator, 
  Percent, 
  FileText, 
  FileCheck, 
  Check, 
  ArrowRight, 
  Clock, 
  ShieldCheck, 
  HelpCircle, 
  Building2, 
  BadgePercent, 
  Users, 
  Sparkles,
  Phone,
  Mail,
  MapPin,
  Laptop
} from 'lucide-react'
import ContactForm from '../components/ContactForm'

import RelatedServices from '../components/RelatedServices'
import FAQSection from '../components/FAQSection'
import { updateMeta } from '../lib/utils'

export default function AccountingHelpPage() {
  useEffect(() => {
    updateMeta(
      "Hjælp til regnskab | Thellefsen Regnskab",
      "Få samlet hjælp til regnskab, bogføring og moms. Vi sikrer overblik over tallene, så du kan fokusere trygt på at drive din virksomhed."
    )
  }, [])

  const packageFeatures = [
    {
      title: "Løbende bogføring af virksomhedens bilag",
      desc: "Løbende registrering og orden i alle dine bilag, så du slipper for bilagsbunker.",
      icon: FileText
    },
    {
      title: "Bankafstemning, hvor bogføringen sammenholdes med banken",
      desc: "Sikrer at alle bevægelser stemmer overens med virkeligheden på din konto.",
      icon: Calculator
    },
    {
      title: "Momsregnskab og momsindberetning ved de relevante terminer",
      desc: "Korrekt beregning og rettidig indberetning til Skattestyrelsen uden overraskelser.",
      icon: Percent
    },
    {
      title: "Årsregnskab og relevante skatteindberetninger efter virksomhedsform",
      desc: "Opstilling af årsrapport og oplysningsskema tilpasset din virksomhedsform.",
      icon: FileCheck
    },
    {
      title: "Adgang til regnskabsprogram uden ekstra omkostninger i pakken",
      desc: "Professionelt regnskabsprogram inkluderet – ingen skjulte softwareudgifter.",
      icon: Laptop
    },
    {
      title: "Løbende spørgsmål og forklaring af virksomhedens regnskabstal",
      desc: "Personlig rådgivning og hurtig afklaring på tal, du måtte være i tvivl om.",
      icon: HelpCircle
    }
  ]

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      
      {/* Hero Section */}
      <section className="bg-white border-b border-stone-200 py-16 lg:py-24 relative overflow-hidden">
        {/* Subtle decorative background accent */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-orange-100/50 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 -mb-20 w-80 h-80 rounded-full bg-stone-100/60 blur-2xl pointer-events-none" />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            {/* Top Tag */}
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-100 text-stone-700 text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6 border border-stone-200">
              <span className="w-2 h-2 rounded-full bg-orange-600"></span>
              Thellefsen Regnskab &bull; Regnskabsservice i hele Danmark
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight leading-tight">
              Få hjælp til regnskab og mere tid til din virksomhed
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-stone-600 leading-relaxed">
              Vil du bruge mindre tid på bilag, moms og regnskab? Hos{' '}
              <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
                Thellefsen Regnskab
              </Link>{' '}
              hjælper vi selvstændige og mindre virksomheder med at samle økonomien og forstå tallene. Vi tilbyder regnskabsservice til virksomheder i Nordsjælland, på Sjælland og i resten af Danmark. Du har direkte kontakt til Peter, som står for opgaven.
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <a
                href="#kontakt-form-section"
                className="inline-flex items-center justify-center rounded-none bg-orange-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a
                href="#regnskabspakke"
                className="inline-flex items-center justify-center rounded-none bg-stone-100 border border-stone-300 px-6 py-3.5 text-sm font-semibold text-stone-800 hover:bg-stone-200 transition-colors"
              >
                Se hvad pakken indeholder
              </a>
            </div>

            {/* Quick highlights */}
            <div className="mt-12 pt-8 border-t border-stone-200 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Direkte kontakt</div>
                  <div className="text-xs text-stone-500">Fast dialog med Peter</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Fast årlig pris</div>
                  <div className="text-xs text-stone-500">Gennemskuelige vilkår</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">I øjenhøjde</div>
                  <div className="text-xs text-stone-500">Sprog du kan forstå</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1: Bogføring og regnskab samlet hos én person */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                  Personlig kontakt & overblik
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight mt-2">
                  Bogføring og regnskab samlet hos én person
                </h2>

                <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg mt-6">
                  <p>
                    Når bogføring og regnskab hænger sammen, bliver det lettere at følge virksomhedens økonomi. Vi hjælper med at registrere indtægter og udgifter, afstemme banken og samle tallene i et regnskab. Det giver et grundlag for at se, hvad virksomheden tjener, og hvilke udgifter der fylder i driften.
                  </p>
                  <p>
                    Du taler med Peter om opgaverne og kan få forklaret de tal, du er i tvivl om. Det gælder både spørgsmål til den daglige økonomi og til årets resultat. Har du især brug for hjælp til bilag og registreringer, kan du{' '}
                    <Link 
                      to="/bogfoering" 
                      className="font-semibold text-orange-600 hover:text-orange-700 underline decoration-orange-300 underline-offset-4 transition-colors"
                    >
                      læse mere om vores hjælp til bogføring
                    </Link>{' '}
                    og få afklaret, hvad du ønsker at overlade til os.
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-stone-200 flex flex-wrap gap-4 text-sm text-stone-600">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-600"></span>
                    <span>Enkeltmandsvirksomheder</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-600"></span>
                    <span>Mindre ApS & selskaber</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-600"></span>
                    <span>Direkte sparring</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile card / Peter */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-stone-200 p-8 shadow-sm relative">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/img-4119.jpg" 
                    alt="Peter Thellefsen - Revisor og ejer af Thellefsen Regnskab ApS" 
                    className="w-20 h-20 object-cover border border-stone-200 shrink-0"
                  />
                  <div>
                    <div className="font-bold text-stone-900 text-lg">Peter Thellefsen</div>
                    <div className="text-sm text-stone-500">Revisor & Ejer</div>
                    <div className="text-xs text-orange-600 font-medium mt-1">Thellefsen Regnskab ApS</div>
                  </div>
                </div>

                <blockquote className="text-stone-700 italic border-l-2 border-orange-500 pl-4 my-4 text-sm leading-relaxed">
                  "Hos mig er du ikke bare et kundenummer i et stort bureau. Vi har en direkte, uformel dialog, hvor du altid kan ringe og få et klart svar på dine tal."
                </blockquote>

                <div className="space-y-2.5 pt-4 border-t border-stone-100 text-xs sm:text-sm text-stone-600">
                  <div className="flex items-center gap-2.5">
                    <Phone className="h-4 w-4 text-orange-600 shrink-0" />
                    <a href="tel:+4531335470" className="hover:text-orange-600 transition-colors font-medium">
                      31 33 54 70
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Mail className="h-4 w-4 text-orange-600 shrink-0" />
                    <a href="mailto:info@tregnskab.dk" className="hover:text-orange-600 transition-colors">
                      info@tregnskab.dk
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <MapPin className="h-4 w-4 text-orange-600 shrink-0" />
                    <span>Strandvangsvej 9, 3060 Espergærde</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 2: Hvad får du i en samlet regnskabspakke? */}
      <section className="py-16 lg:py-24 bg-stone-100/60 border-b border-stone-200" id="regnskabspakke">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
              Alt-i-én løsning
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight mt-2">
              Hvad får du i en samlet regnskabspakke?
            </h2>
            <p className="mt-4 text-stone-600 text-base sm:text-lg leading-relaxed">
              En samlet regnskabspakke er til dig, der ønsker hjælp gennem hele regnskabsåret frem for kun ved årsafslutningen. Vi samler den løbende bogføring, moms og årets regnskab til en fast årlig pris. Du får et tilbud, så du kan tage stilling til både opgaverne og prisen, inden samarbejdet begynder.
            </p>
          </div>

          {/* 6 Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {packageFeatures.map((feat, index) => {
              const IconComp = feat.icon
              return (
                <div key={index} className="bg-white border border-stone-200 p-6 sm:p-8 h-full flex flex-col justify-between shadow-sm hover:border-orange-500/50 hover:shadow-md transition-all duration-300">
                  <div>
                    <div className="h-12 w-12 bg-stone-50 border border-stone-200 flex items-center justify-center text-orange-600 mb-6">
                      <IconComp className="h-6 w-6" />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-stone-900 leading-snug mb-2">
                      {feat.title}
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed">
                      {feat.desc}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-100 flex items-center gap-2 text-xs font-semibold text-stone-500">
                    <Check className="h-4 w-4 text-orange-600" />
                    <span>Inkluderet i pakken</span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Sektionens afrunding */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-white border-l-4 border-orange-600 p-6 sm:p-8 border-y border-r border-stone-200 shadow-sm">
              <p className="text-stone-700 leading-relaxed text-base sm:text-lg">
                Den faste årlige pris gør udgiften til de aftalte regnskabsopgaver lettere at planlægge. Pakken omfatter adgang til regnskabsprogram, men det er også vigtigt at få afklaret arbejdsdelingen. Vi taler derfor om, hvad du selv vil håndtere, og hvilke opgaver du ønsker hjælp til, når vi gennemgår dit behov.
              </p>
            </div>
          </div>



        </div>
      </section>

      {/* Sektion 3 & 4: Regnskabsservice med plads til spørgsmål & Momsregnskab */}
      <section className="py-16 lg:py-24 bg-white border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            
            {/* Kort 1: Regnskabsservice med plads til dine spørgsmål */}
            <div className="bg-stone-50 border border-stone-200 p-8 sm:p-10 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-stone-200 text-xs font-semibold text-stone-600 uppercase tracking-wide mb-4">
                  <HelpCircle className="h-3.5 w-3.5 text-orange-600" />
                  Forståelig rådgivning
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mb-6">
                  Regnskabsservice med plads til dine spørgsmål
                </h3>
                <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg">
                  <p>
                    Du behøver ikke kende alle fagudtryk for at få værdi af dit regnskab. Vi forklarer tallene i et sprog, du kan bruge, og hjælper med spørgsmål om virksomhedens løbende økonomi. Vores regnskabsservice er relevant, uanset om du driver en enkeltmandsvirksomhed eller et mindre selskab.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200/80">
                <div className="text-sm font-semibold text-stone-900 mb-2">
                  Hvad kan du altid spørge om?
                </div>
                <ul className="space-y-2 text-sm text-stone-600">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-orange-600 shrink-0" />
                    <span>Hvad må trækkes fra i virksomheden?</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-orange-600 shrink-0" />
                    <span>Hvordan ser årets overskud ud indtil videre?</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-orange-600 shrink-0" />
                    <span>Hvad skal der sættes til side til skat og moms?</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Kort 2: Momsregnskab med styr på grundlaget */}
            <div className="bg-stone-50 border border-stone-200 p-8 sm:p-10 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-stone-200 text-xs font-semibold text-stone-600 uppercase tracking-wide mb-4">
                  <Percent className="h-3.5 w-3.5 text-orange-600" />
                  Moms & afstemning
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mb-6">
                  Momsregnskab med styr på grundlaget
                </h3>
                <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg">
                  <p>
                    Et momsregnskab bygger på virksomhedens køb og salg. Vi hjælper med at opgøre momsen ud fra bogføringen og indberette ved de relevante terminer. Afstemning er en vigtig del af arbejdet, fordi manglende bilag eller forkerte registreringer kan påvirke det beløb, virksomheden skal betale eller have tilbage.
                  </p>
                  <p>
                    Moms følger ikke altid de samme regler for alle udgifter og aktiviteter. Derfor er det vigtigt at få afklaret tvivl om bilag og momsfradrag frem for at gætte. Du kan spørge os om den løbende håndtering, så der er et bedre grundlag for både bogføringen og den næste momsindberetning.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200/80 flex items-center justify-between text-xs text-stone-500">
                <span>Rettidig indberetning hver termin</span>
                <span className="font-semibold text-stone-700">Undgå gebyrer og fejl</span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Sektion 5: Økonomiadministration efter dit behov */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-stone-200 p-8 sm:p-12 shadow-sm">
            <div className="max-w-3xl">
              <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                Fleksibel bistand
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-2 mb-6">
                Økonomiadministration efter dit behov
              </h2>
              <p className="text-stone-600 text-base sm:text-lg leading-relaxed mb-8">
                Har du også brug for hjælp til lønadministration, fakturering eller debitorstyring, kan vi tale om at inddrage det i samarbejdet. Økonomiadministration handler ikke kun om at registrere det, der er sket. Det handler også om at holde overblik over udestående betalinger og de opgaver, der følger med driften.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-stone-50 border border-stone-200">
                  <div className="font-bold text-stone-900 text-sm mb-1">Lønadministration</div>
                  <div className="text-xs text-stone-500">Lønsedler, indberetning til eIndkomst og feriekonto.</div>
                </div>
                <div className="p-4 bg-stone-50 border border-stone-200">
                  <div className="font-bold text-stone-900 text-sm mb-1">Fakturering</div>
                  <div className="text-xs text-stone-500">Udsendelse af fakturaer og opfølgning på salg.</div>
                </div>
                <div className="p-4 bg-stone-50 border border-stone-200">
                  <div className="font-bold text-stone-900 text-sm mb-1">Debitorstyring</div>
                  <div className="text-xs text-stone-500">Overblik over forfaldne betalinger og rykkerprocedure.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: Klargøring til årsafslutning & Hvornår har virksomheden brug for en revisor? */}
      <section className="py-16 lg:py-24 bg-white border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-7 space-y-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                  Årsafslutning & Årsregnskab
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-2">
                  Få dit regnskab klar til årsafslutning og din revisor
                </h2>
                <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg mt-6">
                  <p>
                    Når årets bilag er bogført, og banken er afstemt løbende, er der et bedre udgangspunkt for årsafslutningen. Vi gennemgår årets bogføring og hjælper med årsregnskabet eller klargøring til din revisor. Her samles blandt andet resultatopgørelsen, som viser indtægter og udgifter, og balancen, som viser virksomhedens aktiver og gæld.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/aarsrapport-og-aarsregnskab"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4"
                  >
                    Læs mere om vores hjælp til årsrapport og årsregnskab
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-stone-50 border border-stone-200 p-8 relative">
                <div className="flex items-center gap-3 mb-4 text-stone-900 font-bold text-xl">
                  <Building2 className="h-6 w-6 text-orange-600 shrink-0" />
                  <h3>Hvornår har din virksomhed brug for en revisor?</h3>
                </div>
                
                <div className="space-y-4 text-stone-600 leading-relaxed text-sm sm:text-base">
                  <p>
                    Kravene afhænger af din virksomhedsform. En enkeltmandsvirksomhed skal normalt ikke indsende en årsrapport til Erhvervsstyrelsen, men skal stadig opgøre resultatet til skat. Har virksomheden pligt til revision, skal en godkendt revisor udføre den. <Link to="/aarsrapport-og-aarsregnskab" className="font-semibold text-orange-600 hover:text-orange-700 underline decoration-orange-300 underline-offset-4">Læs mere om vores hjælp til årsrapport og årsregnskab</Link>, hvis du søger hjælp til årets afslutning.
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-stone-200 text-xs text-stone-500">
                  Vi rådgiver dig gerne om, hvilke specifikke krav der gælder for netop din virksomhed.
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Sektion 7: Hvad koster hjælp til dit regnskab? */}
      <section className="py-16 lg:py-20 bg-stone-100/70">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center p-3 bg-white border border-stone-200 mb-6 text-orange-600">
              <BadgePercent className="h-8 w-8" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight">
              Hvad koster hjælp til dit regnskab?
            </h2>
            <p className="mt-6 text-stone-600 text-base sm:text-lg leading-relaxed">
              Prisen afhænger af opgavernes omfang og af, hvor meget du selv vil stå for. Antallet af bilag og behovet for eksempelvis moms, løn og fakturering er relevante forhold at få afklaret. Vi giver et konkret tilbud frem for en standardpris, der ikke tager højde for din virksomheds hverdag.
            </p>
            <div className="mt-8">
              <a
                href="#kontakt-form-section"
                className="inline-flex items-center justify-center rounded-none bg-orange-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Indhent et uforpligtende tilbud her
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 8: FAQ */}
      <FAQSection
        title="Ofte stillede spørgsmål om regnskabsservice"
        subtitle="Få svar på de mest almindelige spørgsmål om vores samlede regnskabspakker og rådgivning."
        items={[
          {
            question: "Hvad er fordelen ved at vælge en samlet regnskabsservice frem for løse ydelser?",
            answer: "Ved at samle bogføring, moms og årsregnskab hos én fast rådgiver undgår du, at vigtige bilag og fradrag falder mellem to stole ved årets afslutning. Du får en sammenhængende proces gennem hele året, hvor tallene altid er ajourført, og du sparer både tid og penge, fordi årsafslutningen forløber langt hurtigere og uden ubehagelige overraskelser."
          },
          {
            question: "Hvordan sikrer I, at alle lovpligtige deadlines overholdes?",
            answer: "Vi fører en fast tidsplan for din virksomhed og holder nøje øje med samtlige frister hos Skattestyrelsen og Erhvervsstyrelsen. Vi giver dig besked i god tid, når der skal afstemmes eller godkendes tal for moms og skat, så du aldrig risikerer rykkergebyrer, bøder eller uoverskuelige forsinkelser i din administration."
          },
          {
            question: "Kan jeg få løbende rådgivning og sparring i løbet af året?",
            answer: "Ja, løbende faglig sparring er en integreret del af vores regnskabsservice. Uanset om du overvejer større investeringer, ændring af selskabsform eller har spørgsmål til fradragsregler og optimering af selskabsskat, er du altid velkommen til at række ud til Peter for et klart og pragmatisk svar på et sprog, du forstår."
          },
          {
            question: "Hvad skal jeg selv gøre i det daglige, når I passer mit regnskab?",
            answer: "Din primære opgave er blot at videresende eller uploade dine købsbilag og fakturere dine kunder via regnskabsprogrammet. Vi tager os af resten: bilagsregistrering, bankafstemning, momsregnskab og det endelige årsregnskab, så du kan fokusere din energi på din forretning og dine egne kunder uden regnskabsbøvl."
          },
          {
            question: "Hvad sker der, hvis min virksomhed vokser eller får flere bilag i løbet af året?",
            answer: "Vores aftaler er fleksible og tilpasses altid virksomhedens reelle aktivitetsniveau. Hvis dit bilagsvolumen stiger væsentligt, eller din forretning ændrer struktur, tager vi en åben dialog i god tid og justerer aftalen derefter. Du bliver aldrig mødt af skjulte ekstraregninger eller uforudsete gebyrer uden forudgående aftale."
          }
        ]}
      />

      {/* Sektion 9: Relaterede ydelser */}
      <RelatedServices currentPath="/regnskabsservice" title="Se også vores andre ydelser" />

      {/* Sektion 10: Kontakt Peter & Formular */}
      <section id="kontakt-form-section" className="py-16 lg:py-24 bg-white border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Tekst og info */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                  Uforpligtende samtale
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-2">
                  Kontakt Peter for en uforpligtende samtale om dit regnskab
                </h2>

                <p className="text-stone-600 text-base sm:text-lg leading-relaxed mt-4">
                  Kontakt Peter for en uforpligtende samtale om dit regnskab. Vi tager udgangspunkt i din virksomhed og afklarer, om du har brug for enkelte opgaver eller en samlet løsning. Vi holder til i Espergærde og hjælper kunder i hele landet. Fortæl os om dit behov, og få et tilbud.
                </p>

                <div className="mt-8 space-y-4 pt-6 border-t border-stone-200">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-bold text-stone-900">Kontor i Espergærde</div>
                      <div className="text-xs text-stone-600">Strandvangsvej 9, 3060 Espergærde (kunder i hele Danmark)</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-bold text-stone-900">Ring direkte til Peter</div>
                      <a href="tel:+4531335470" className="text-xs text-orange-600 font-semibold hover:underline">
                        31 33 54 70
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-bold text-stone-900">Send en e-mail</div>
                      <a href="mailto:info@tregnskab.dk" className="text-xs text-stone-600 hover:text-orange-600">
                        info@tregnskab.dk
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Kontaktformular */}
            <div className="lg:col-span-7">
              <ContactForm 
                defaultService="regnskabsservice" 
                title="Få et uforpligtende tilbud" 
                subtitle="Fortæl kort om din virksomhed og dine ønsker, så vender Peter tilbage hurtigst muligt." 
              />
            </div>

          </div>

        </div>
      </section>

    </div>
  )
}
